import {
  CrystalDTO,
  CrystalFilesUploadDTO,
  FileUploadDTO,
  CrystalWithResultDTO,
  ICrystalDTO,
} from 'business/models/Models';
import CrystalService from 'business/services/CrystalService';
import { CrystalDataDTO } from 'components/synchrotron/constants';
import { DrawingType } from 'components/well/drawingManager/constants';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import React, { SetStateAction, useCallback, useEffect, useState } from 'react';
import { useRMDispatch } from 'redux/hooks';
import {
  useLastSelectedWellNavigationDetails,
  useSelectedDropImageParameters,
} from 'redux/hooks/experiment';
import { updateDrawing } from 'redux/reducers/wellReducer';
import { getPlainCrystalData } from 'transformers/synchrotronTrip';
import { downloadFile, getDateISOFormat, showAlertSnackBar } from 'utils/Global';

export type EditCrystalDTO = {
  name?: string;
  dateFrozen?: string;
  notes?: string;
};

const crystalService = new CrystalService();

const useCrystalDetails = (crystalId: number) => {
  const [isLoading, setIsLoading] = useState<boolean>();
  const [crystal, setCrystal] = useState<CrystalDTO>();
  const [refetch, setRefetch] = useState<boolean>();

  const refetchCrystalDetails = () => {
    setRefetch(!refetch);
  };

  useEffect(() => {
    if (crystalId) {
      const fetchData = async () => {
        setIsLoading(true);
        return await crystalService.getCrystalDetails(crystalId);
      };
      fetchData().then((data) => {
        setCrystal(data);
        setIsLoading(false);
      });
    }
  }, [crystalId, refetch]);

  return { crystal, setCrystal, isLoading, refetchCrystalDetails };
};

const useCrystalEdit = (
  crystal: CrystalDTO,
  setCrystal: React.Dispatch<SetStateAction<CrystalDTO>>,
) => {
  const dispatch = useRMDispatch();
  const [editCrystal, setEditCrystal] = useState<EditCrystalDTO>();
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const { wellNum: wellNumber, plateID: plateId } = wellNavigationDetails || {};
  const { dropNumber, regionName } = selectedDropImageParameters || {};

  const handleChange = (e: any) => {
    setIsDirty(true);
    setEditCrystal({ ...editCrystal, [e.target.name]: e.target.value });
  };

  const useSetEditedCrystal = (crystal: CrystalDTO) => {
    dispatch(
      updateDrawing({
        plateId,
        wellNumber,
        dropNumber,
        regionName,
        drawingType: DrawingType.CRYSTAL,
        id: crystal.id,
        drawingUpdateDetails: {
          name: crystal.name,
          dateFrozen: crystal.dateFrozen,
          notes: crystal.notes,
        },
      }),
    );
  };

  const handleSave = async () => {
    if (editCrystal.name.trim().length <= 0) {
      showAlertSnackBar(
        EAlertType.WARNING,
        ESnackBarPosition.TOP,
        'Crystal name cannot be empty.',
      );
      return;
    }
    setCrystal((prev: any) => ({ ...prev, ...editCrystal }));
    setIsSaving(true);
  };

  const handleDiscard = () => {
    setIsDirty(false);
    setEditCrystal({
      name: crystal?.name,
      dateFrozen: getDateISOFormat(crystal?.dateFrozen),
      notes: crystal?.notes,
    });
  };

  useEffect(() => {
    if (isSaving) {
      crystalService.updateCrystal(crystal).then(() => {
        setIsDirty(false);
        setIsSaving(false);
        useSetEditedCrystal(crystal);
        showAlertSnackBar(
          EAlertType.SUCCESS,
          ESnackBarPosition.TOP,
          'Crystal successfully saved.',
        );
      });
    }
  }, [isSaving]);

  useEffect(() => {
    setCrystal(crystal);
    setEditCrystal({
      name: crystal?.name,
      dateFrozen: getDateISOFormat(crystal?.dateFrozen),
      notes: crystal?.notes,
    });
  }, [crystal]);

  return { editCrystal, isDirty, handleChange, handleSave, handleDiscard };
};

const useCrystalAttachment = (crystalId: number, refetch) => {
  const handleAttachmentUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files ? e.target.files[0] : null;
      if (file && file.size <= 10 * 1024 * 1024) {
        const filesUploadDto: CrystalFilesUploadDTO = new CrystalFilesUploadDTO();
        const filesDto: FileUploadDTO[] = new Array<FileUploadDTO>();
        const reader = new FileReader();
        reader.onload = (r) => {
          const result = r.target.result;
          filesUploadDto.crystalId = crystalId;
          const fileUpload: FileUploadDTO = new FileUploadDTO();
          fileUpload.fileName = file.name;
          fileUpload.fileData = result.toString().split(',')[1];
          filesDto.push(fileUpload);
          filesUploadDto.files = filesDto;
          crystalService
            .uploadCrystalAttachments(filesUploadDto)
            .then((data) => {
              showAlertSnackBar(
                EAlertType.SUCCESS,
                ESnackBarPosition.TOP,
                'File successfully attached.',
              );
              refetch();
              e.target.value = null;
            })
            .catch((error) => {
              showAlertSnackBar(
                EAlertType.ERROR,
                ESnackBarPosition.TOP,
                'File upload failed. ' + error.statusText,
              );
            });
        };

        reader.readAsDataURL(file);
      } else {
        showAlertSnackBar(
          EAlertType.ERROR,
          ESnackBarPosition.TOP,
          'File size exceeds 10MB. Please upload a smaller file.',
        );
        e.target.value = null;
      }
    },
    [crystalId, refetch],
  );

  const handleAttachmentDownload = useCallback(
    async (fileId: number) => {
      const file = await crystalService.downloadCrystalAttachment(fileId);
      downloadFile(file.data, file.fileName);
    },
    [crystalId],
  );

  return {
    handleAttachmentUpload,
    handleAttachmentDownload,
  };
};

const useUnassignedCrystals = () => {
  const [unassignedCrystals, setUnassignedCrystals] = useState<CrystalDataDTO[]>();
  const [refetch, setRefetch] = useState<boolean>();

  const refetchUnassignedCrystals = () => {
    setRefetch(!refetch);
  };

  useEffect(() => {
    const fetchUnassignedCrystals = async () => {
      return getPlainCrystalData(await crystalService.getAllUnassignedCrystals());
    };
    fetchUnassignedCrystals().then((data) => {
      setUnassignedCrystals(data);
    });
  }, [refetch]);

  return { unassignedCrystals, refetchUnassignedCrystals };
};

const useCollectedCrystals = (tripId: number) => {
  const [collectedCrystals, setCollectedCrystals] = useState<CrystalWithResultDTO[]>();
  const [refetch, setRefetch] = useState<boolean>(false);

  const refetchCollectedCrystals = () => {
    setRefetch(!refetch);
  };

  useEffect(() => {
    const fetchCollectedCrystals = async () => {
      return await crystalService.getCollectedCrystalsByTripId(tripId);
    };
    fetchCollectedCrystals().then((data) => {
      setCollectedCrystals(data);
    });
  }, [refetch]);

  return { collectedCrystals, refetchCollectedCrystals };
};

const useCrystalsByRegion = (regionId: number) => {
  const [crystals, setCrystals] = useState<ICrystalDTO[]>([]);

  useEffect(() => {
    if (regionId) {
      crystalService.GetCrystalInfoByRegion(regionId).then((res) => {
        setCrystals(res);
      });
    }
  }, [regionId]);
  return { crystals };
};

export {
  useCrystalDetails,
  useCrystalEdit,
  useCrystalAttachment,
  useUnassignedCrystals,
  useCollectedCrystals,
  useCrystalsByRegion,
};
